import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Calendar, Users, Package, MapPin, Clock, FileText, CheckSquare, Receipt, Tag, CreditCard, FileSignature, Inbox, CalendarPlus } from "lucide-react";
import { formatDate, formatTime } from "@/lib/dateFormat";
import { formatCurrency } from "@/lib/data";
import { EventDetail } from "./types";

interface EventDetailsProps {
  event: EventDetail;
  tasksCount?: number;
  invoicesCount?: number;
}

/** A date-only value (no time part) or a midnight timestamp carries no real time. */
function hasTime(value?: string | null): boolean {
  if (!value || !value.includes("T")) return false;
  const time = value.split("T")[1] || "";
  return !time.startsWith("00:00:00");
}

function sameDay(a?: string | null, b?: string | null): boolean {
  return !!a && !!b && a.slice(0, 10) === b.slice(0, 10);
}

/** Inclusive number of calendar days the event spans. */
function spanDays(start?: string | null, end?: string | null): number {
  if (!start || !end) return 1;
  const s = Date.parse(start.slice(0, 10));
  const e = Date.parse(end.slice(0, 10));
  if (isNaN(s) || isNaN(e) || e < s) return 1;
  return Math.round((e - s) / 86_400_000) + 1;
}

const titleCase = (s?: string | null) => (s || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
const PAYMENT_LABEL: Record<string, string> = { paid: "Paid in full", partial: "Partly paid", unpaid: "Unpaid", overdue: "Overdue" };

function Row({ icon, tone, label, children }: { icon: ReactNode; tone: string; label: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border ${tone}`}>{icon}</div>
      <div className="min-w-0">
        <p className="text-[11px] text-stone-500 uppercase tracking-wide">{label}</p>
        <div className="text-sm font-semibold text-stone-800 break-words">{children}</div>
      </div>
    </div>
  );
}

export default function EventDetails({ event, tasksCount = 0, invoicesCount = 0 }: EventDetailsProps) {
  const multiDay = !!event.end_date && !sameDay(event.start_date, event.end_date);
  const days = spanDays(event.start_date, event.end_date);
  const showStartTime = hasTime(event.start_date);
  const showEndTime = hasTime(event.end_date);

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden shadow-sm">
      <div className="px-4 py-3 bg-gradient-to-r from-amber-50 to-amber-100/50 border-b border-amber-100">
        <h3 className="font-bold text-stone-800 text-sm flex items-center gap-2">
          <Calendar className="w-4 h-4 text-amber-600" />
          Event Details
        </h3>
      </div>

      <div className="p-4 space-y-4">
        <Row icon={<Calendar className="w-4 h-4 text-amber-600" />} tone="bg-amber-50 border-amber-100" label={multiDay ? "Dates" : "Date"}>
          {formatDate(event.start_date)}
          {multiDay && <> – {formatDate(event.end_date)}</>}
          {multiDay && <span className="block text-xs font-normal text-stone-500">{days} days</span>}
        </Row>

        {(showStartTime || showEndTime) && (
          <Row icon={<Clock className="w-4 h-4 text-blue-600" />} tone="bg-blue-50 border-blue-100" label="Time">
            {showStartTime ? formatTime(event.start_date) : "—"}
            {showEndTime && <> – {formatTime(event.end_date)}{multiDay && <span className="font-normal text-stone-500"> (last day)</span>}</>}
          </Row>
        )}

        {event.event_type && (
          <Row icon={<Tag className="w-4 h-4 text-violet-600" />} tone="bg-violet-50 border-violet-100" label="Event Type">
            {titleCase(event.event_type)}
          </Row>
        )}

        <Row icon={<Users className="w-4 h-4 text-indigo-600" />} tone="bg-indigo-50 border-indigo-100" label="Guest Count">
          {event.guest_count ? `${event.guest_count} guests` : <span className="font-normal text-stone-400">Not set</span>}
        </Row>

        <Row icon={<MapPin className="w-4 h-4 text-rose-600" />} tone="bg-rose-50 border-rose-100" label="Venue">
          {event.venue ? (
            <>
              {event.venue.name}
              {event.venue.location && <span className="block text-xs font-normal text-stone-500">{event.venue.location}</span>}
              {event.venue.capacity ? <span className="block text-xs font-normal text-stone-500">Capacity {event.venue.capacity}</span> : null}
            </>
          ) : (
            <span className="font-normal text-stone-400">No venue assigned</span>
          )}
        </Row>

        <Row icon={<Package className="w-4 h-4 text-emerald-600" />} tone="bg-emerald-50 border-emerald-100" label="Package">
          {event.package?.name || <span className="font-normal text-stone-400">Custom (no package)</span>}
        </Row>

        {event.payment_status && event.status !== "cancelled" && Number(event.financials?.final_revenue ?? 1) > 0 && (
          <Row icon={<CreditCard className="w-4 h-4 text-teal-600" />} tone="bg-teal-50 border-teal-100" label="Payment">
            <span className={event.payment_status === "overdue" ? "text-red-700" : undefined}>
              {PAYMENT_LABEL[event.payment_status] || titleCase(event.payment_status)}
            </span>
            {event.payment_status !== "paid" && Number(event.financials?.customer?.balance) > 0 && (
              <span className="block text-xs font-normal text-stone-500">
                {formatCurrency(event.financials?.customer?.balance)} due
                {event.payment_status === "overdue" ? " — the event date has passed" : ""}
              </span>
            )}
          </Row>
        )}

        {(event.quotation || event.enquiry) && (
          <div className="grid grid-cols-1 gap-2 pt-1">
            {event.quotation && (
              <Link
                to={`/quotations/detail?id=${event.quotation.id}`}
                className="flex items-center gap-2 min-h-10 px-3 py-2 rounded-lg border border-stone-200 hover:bg-stone-50 text-sm"
              >
                <FileSignature className="w-4 h-4 text-stone-500 flex-shrink-0" />
                <span className="text-stone-500">From quotation</span>
                <span className="font-semibold text-stone-800 truncate">{event.quotation.quotation_number || `#${event.quotation.id}`}</span>
              </Link>
            )}
            {event.enquiry && (
              <Link
                to={`/enquiries/detail?id=${event.enquiry.id}`}
                className="flex items-center gap-2 min-h-10 px-3 py-2 rounded-lg border border-stone-200 hover:bg-stone-50 text-sm"
              >
                <Inbox className="w-4 h-4 text-stone-500 flex-shrink-0" />
                <span className="text-stone-500">From enquiry</span>
                <span className="font-semibold text-stone-800 truncate">
                  #{event.enquiry.id}{event.enquiry.lead_source ? ` · ${titleCase(event.enquiry.lead_source)}` : ""}
                </span>
              </Link>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2 p-2 bg-violet-50 rounded-lg border border-violet-100">
            <CheckSquare className="w-4 h-4 text-violet-600" />
            <div>
              <p className="text-[10px] text-violet-600 uppercase tracking-wide">Tasks</p>
              <p className="text-sm font-semibold text-violet-800">{tasksCount}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg border border-amber-100">
            <Receipt className="w-4 h-4 text-amber-600" />
            <div>
              <p className="text-[10px] text-amber-600 uppercase tracking-wide">Invoices</p>
              <p className="text-sm font-semibold text-amber-800">{invoicesCount}</p>
            </div>
          </div>
        </div>

        {event.notes && (
          <div className="pt-4 border-t border-stone-100">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-stone-50 flex items-center justify-center flex-shrink-0 border border-stone-200">
                <FileText className="w-4 h-4 text-stone-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] text-stone-500 uppercase tracking-wide">Notes</p>
                <p className="text-sm text-stone-700 mt-1 whitespace-pre-line break-words">{event.notes}</p>
              </div>
            </div>
          </div>
        )}

        {event.created_at && (
          <p className="flex items-center gap-1.5 text-xs text-stone-400 pt-1">
            <CalendarPlus className="w-3.5 h-3.5" />
            Created {formatDate(event.created_at)}
          </p>
        )}
      </div>
    </div>
  );
}
